import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import queryString from "query-string";

import {
  QueriesResponseModel,
  QueryModel,
  QueryScopeType,
  QueryStatusType,
} from "@interfaces/index";
import { QUERY_WIDTHS } from "@utils/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { useQueryGetQueries } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  PageContentContainer,
  PageContentTitle,
  RangePicker,
  BusinessSelector,
  DesktopPageTitle,
  Button,
  PlusSvg,
  TableTabs,
  TableSubtabs,
  QueryItem,
} from "@components/index";
import { NewQueryDrawer } from "@pages/index";

export const QueriesPage = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);

  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const [businessId, setBusinessId] = useState("");

  const [activeTab, setActiveTab] = useState<QueryScopeType | "">("");
  const [activeSubtab, setActiveSubtab] = useState<QueryStatusType | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [redirectNeeds, setRedirectNeeds] = useState(false);

  const [tabs, setTabs] = useState<QueriesResponseModel["tabs"]>([]);
  const [subtabs, setSubtabs] = useState<QueriesResponseModel["subtabs"]>([]);
  const [raisedBy, setRaisedBy] = useState<QueriesResponseModel["raisedBy"]>(
    [],
  );
  const [raisedByFbtc, setRaisedByFbtc] = useState<QueryModel[]>([]);
  const [raisedByClient, setRaisedByClient] = useState<QueryModel[]>([]);

  const [showNewQueryDrawer, setShowNewQueryDrawer] = useState(false);

  const [openQueryId, setOpenQueryId] = useState("");
  const handleToggleQuery = (uniqueId: string) => {
    setOpenQueryId((prev) => (prev === uniqueId ? "" : uniqueId));
    setRedirectNeeds(true);
  };

  useEffect(() => {
    const params = queryString.parse(location.search);
    setBusinessId(
      (params?.businessId as string) ||
        defaultBusiness?.uniqueId ||
        businesses[0]?.uniqueId ||
        "",
    );
    setActiveTab((params?.activeTab as QueryScopeType) || "");
    setActiveSubtab((params?.activeSubtab as QueryStatusType) || "");
    setStartDate((params?.startDate as string) || "");
    setEndDate((params?.endDate as string) || "");
    setOpenQueryId((params?.queryId as string) || "");
  }, [location, defaultBusiness, businesses]);

  useEffect(() => {
    if (!activeTab && tabs.length) {
      setActiveTab(tabs[0].value);
    }
  }, [activeTab, tabs]);

  useEffect(() => {
    if (!activeSubtab && subtabs.length) {
      setActiveSubtab(subtabs[0].value);
    }
  }, [activeSubtab, subtabs]);

  const {
    isPending: isPendingGetQueries,
    isSuccess: isSuccessGetQueries,
    isStale: isStaleGetQueries,
    data: dataGetQueries,
    isError: isErrorGetQueries,
    error: errorGetQueries,
  } = useQueryGetQueries({
    businessId,
    startDate,
    endDate,
    scope: activeTab,
    status: activeSubtab,
  });

  useEffect(() => {
    if (isSuccessGetQueries && dataGetQueries) {
      setTabs(dataGetQueries.tabs);
      setSubtabs(dataGetQueries.subtabs);
      setRaisedBy(dataGetQueries.raisedBy);
      setRaisedByFbtc(dataGetQueries.raisedByFbtc);
      setRaisedByClient(dataGetQueries.raisedByClient);
    }
  }, [isSuccessGetQueries, dataGetQueries]);

  useEffect(() => {
    if (isErrorGetQueries && errorGetQueries) {
      notify("error", errorGetQueries.message);
    }
  }, [isErrorGetQueries, errorGetQueries, notify]);

  useEffect(() => {
    setState({
      isLoading: isStaleGetQueries && isPendingGetQueries,
    });
  }, [isStaleGetQueries, isPendingGetQueries, setState]);

  useEffect(() => {
    if (!redirectNeeds) {
      return;
    }
    setRedirectNeeds(false);
    const params = queryString.stringify(
      {
        businessId,
        activeTab,
        activeSubtab,
        startDate,
        endDate,
        queryId: openQueryId,
      },
      { skipEmptyString: true },
    );
    navigate(`?${params}`);
  }, [
    redirectNeeds,
    navigate,
    businessId,
    activeTab,
    activeSubtab,
    startDate,
    endDate,
    openQueryId,
  ]);

  const handleSelectBusiness = (businessId: string) => {
    setRedirectNeeds(true);
    setBusinessId(businessId);
  };

  const handleSelectTab = (value: QueryScopeType) => {
    setRedirectNeeds(true);
    setActiveTab(value);
    setOpenQueryId("");
  };

  const handleSelectSubtab = (value: QueryStatusType) => {
    setRedirectNeeds(true);
    setActiveSubtab(value);
    setOpenQueryId("");
  };

  const handleSetStartDate = (startDate: string) => {
    setRedirectNeeds(true);
    setStartDate(startDate);
  };

  const handleSetEndDate = (endDate: string) => {
    setRedirectNeeds(true);
    setEndDate(endDate);
  };

  return (
    <div className="md:pt-6">
      {!isMobile && <DesktopPageTitle title="Queries" />}
      <div className="flex flex-col justify-between gap-y-4 px-4 pt-4 md:flex-row md:px-10 md:pt-6">
        <BusinessSelector
          height="small"
          width="w-full md:w-64"
          value={businessId}
          onSetBusiness={handleSelectBusiness}
          dataTestid="filter_businessSelector"
        />
        <RangePicker
          startDate={startDate}
          endDate={endDate}
          prefix="queriesPage"
          onSetStartDate={handleSetStartDate}
          onSetEndDate={handleSetEndDate}
          showPeriods={true}
          dataTestid="filter_dateRangeSelector"
        />
      </div>
      <PageContentContainer>
        <div className="flex flex-col justify-between pb-2 md:flex-row md:pb-0">
          {!isMobile && <PageContentTitle title="Queries" />}
          <Button
            title="New query"
            leftIcon={<PlusSvg />}
            className="w-full md:w-fit md:px-5"
            height="small"
            onClick={() => setShowNewQueryDrawer(true)}
            dataTestid="button_newQuery"
          />
        </div>
        <TableTabs
          tabs={tabs}
          activeTab={activeTab}
          dataTestid="tabs_queriesPage"
          className="border-b border-lightGray"
          onClick={(value) => handleSelectTab(value as QueryScopeType)}
        />
        <TableSubtabs
          tabs={subtabs}
          activeTab={activeSubtab}
          dataTestid="subtabs_queriesPage"
          className="border-b border-lightGray pt-4"
          onClick={(value) => handleSelectSubtab(value as QueryStatusType)}
        />
        <div className="flex gap-x-6 pt-6">
          <div className="w-1/2">
            <div className="mb-3 text-300 font-bold text-black-100">
              {raisedBy[0]?.label}
            </div>
            <div className="flex max-h-[600px] flex-col gap-y-2 overflow-y-auto">
              {!raisedByFbtc.length ? (
                <div className="pt-4 text-center text-black-54">No queries</div>
              ) : (
                raisedByFbtc.map((query) => (
                  <QueryItem
                    key={query.uniqueId}
                    query={query}
                    isOpen={openQueryId === query.uniqueId}
                    onToggle={() => handleToggleQuery(query.uniqueId)}
                  />
                ))
              )}
            </div>
          </div>
          <div className="w-1/2">
            <div className="mb-3 text-300 font-bold text-black-100">
              {raisedBy[1]?.label}
            </div>
            <div className="flex max-h-[600px] flex-col gap-y-2 overflow-y-auto">
              {!raisedByClient.length ? (
                <div className="pt-4 text-center text-black-54">No queries</div>
              ) : (
                raisedByClient.map((query) => (
                  <QueryItem
                    key={query.uniqueId}
                    query={query}
                    isOpen={openQueryId === query.uniqueId}
                    onToggle={() => handleToggleQuery(query.uniqueId)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </PageContentContainer>
      <NewQueryDrawer
        businessId={businessId}
        startDate={startDate}
        endDate={endDate}
        show={showNewQueryDrawer}
        onClose={() => setShowNewQueryDrawer(false)}
      />
    </div>
  );
};
